import React from 'react';
import { Box, Container, Grid, sxToStyle, Typography } from '../../../../components/ui/LayoutPrimitives';
import { Snackbar } from '../../../../components/ui/Feedback';
import {
  ArrowLeft as ArrowBack,
  Minus as Remove,
  Plus as Add,
  ShieldCheck as VerifiedUser,
  ShoppingBag,
  Trash2 as DeleteOutline,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TailSpin } from 'react-loading-icons';
import RetailDiscountCode from '../../../payment/RetailDiscountCode';

const Button = ({
  children,
  className = '',
  disabled,
  fullWidth,
  size,
  startIcon,
  style,
  sx,
  type = 'button',
  variant,
  ...props
}) => (
  <button
    type={type}
    className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${fullWidth ? 'w-full' : ''} ${size === 'large' ? 'min-h-12 px-6 text-base' : ''} ${variant === 'contained' ? 'bg-slate-900 text-white' : 'bg-transparent'} ${className}`}
    disabled={disabled}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {startIcon}
    {children}
  </button>
);

const IconButton = ({ children, className = '', disabled, style, sx, type = 'button', ...props }) => (
  <button
    type={type}
    className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    disabled={disabled}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {children}
  </button>
);

const Backdrop = ({ children, open, style, sx }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ ...sxToStyle(sx), ...(style || {}) }}>
      {children}
    </div>
  );
};

const Divider = ({ className = '', style, sx }) => (
  <hr className={`border-0 border-t border-slate-200 ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }} />
);

const SFCart = ({
  items = [],
  totalAmount = 0,
  shippingFee = 0,
  tax = 0,
  currencySymbol = '$',
  isLoading = false,
  isProcessing = false,
  alertMessage = { open: false, severity: 'info', message: '' },
  onRemoveItem,
  onCheckout,
  onContinueShopping,
  onEmptyCart,
  onQuantityChange,
  onCloseAlert,
  discountCode,
  appliedDiscount,
  discountAmount = 0,
  checkoutTotal,
  discountError,
  isApplyingDiscount,
  onDiscountCodeChange,
  onApplyDiscount,
  onRemoveDiscount,
}) => {
  const itemCount = items?.reduce((total, item) => total + item.quantity, 0) || 0;
  const finalTotal = Number.isFinite(Number(checkoutTotal))
    ? Number(checkoutTotal)
    : totalAmount + shippingFee + tax;

  // Luxury Gold Color Scheme
  const luxuryStyles = {
    gold: '#B88E2F',
    goldLight: '#D4AF37',
    goldDark: '#996515',
    charcoal: '#2C2C2C',
    ivory: '#F8F5F0',
    slate: '#5A5A5A',
    success: '#4CAF50'
  };

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    },
    hover: { 
      y: -2,
      boxShadow: '0 10px 30px rgba(184, 142, 47, 0.15)'
    },
    exit: { opacity: 0, x: -50 }
  };

  return (
   <Box sx={{ 
  minHeight: '100vh',
  backgroundColor: luxuryStyles.ivory,
  py: { xs: 2, md: 3 }, // 🔽 Reduce vertical padding
  px: { xs: 2, md: 1 }
}}>

      {/* Loading Overlay */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(44, 44, 44, 0.95)'
        }}
        open={isLoading || isProcessing}
      >
        <Box textAlign="center">
          <TailSpin stroke={luxuryStyles.goldLight} speed={0.8} />
          <Typography variant="h6" mt={3} color="white" sx={{ fontFamily: 'serif' }}>
            {isProcessing ? 'Securing your luxury items...' : 'Curating your collection...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Alert Notifications */}
      <Snackbar
        autoHideDuration={4500}
        className="font-serif shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
        isOpen={alertMessage.open}
        message={alertMessage.message}
        onClose={onCloseAlert}
        placement="top"
        tone={alertMessage.severity}
      />

<Container maxWidth="xl" sx={{ px: { xs: 0, md: 2 }, mt: 1 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 4, md: 3 },
          px: { xs: 2, md: 0 }
        }}>
          <Button
            startIcon={<ArrowBack />}
onClick={() => window.history.back()}
            sx={{
              color: luxuryStyles.charcoal,
              textTransform: 'none',
              fontFamily: 'serif',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(184, 142, 47, 0.08)'
              }
            }}
          >
            Continue Shopping
          </Button>
          <Typography
            variant="h1"
            sx={{
              ml: 4,
              fontWeight: 500,
              color: luxuryStyles.charcoal,
              fontSize: { xs: '1.8rem', md: '2.4rem' },
              fontFamily: 'serif',
              letterSpacing: '0.5px'
            }}
          >
            Your Luxury Selection
           
          </Typography>
        </Box>

        {items?.length > 0 ? (
          <Grid container spacing={4}>
            {/* Items Column */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ 
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                border: `1px solid ${luxuryStyles.ivory}`
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 3,
                  borderBottom: `1px solid ${luxuryStyles.ivory}`,
                  backgroundColor: luxuryStyles.ivory
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500,
                    color: luxuryStyles.charcoal,
                    fontFamily: 'serif',
                    fontSize: '1.2rem'
                  }}>
                    {itemCount} Premium {itemCount === 1 ? 'Piece' : 'Pieces'}
                  </Typography>
                  <Button
                    onClick={onEmptyCart}
                    sx={{
                      color: luxuryStyles.slate,
                      textTransform: 'none',
                      fontFamily: 'serif',
                      '&:hover': {
                        color: luxuryStyles.goldDark,
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    Clear All
                  </Button>
                </Box>

                <Box sx={{ p: { xs: 1, md: 2 } }}>
                  <AnimatePresence>
                    {items.map((item, index) => (
                      <motion.div
                        key={item.product.primaryId}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        variants={itemVariants}
                        exit="exit"
                        layout
                      >
                        <Box sx={{
                          display: 'flex',
                          p: 3,
                          mb: 2,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: `1px solid ${luxuryStyles.ivory}`,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: luxuryStyles.goldLight
                          }
                        }}>
                          {/* Product Image */}
                          <Box sx={{
                            width: 140,
                            height: 140,
                            flexShrink: 0,
                            mr: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '6px'
                          }}>
                         <Box
  component="img"
  src={
    item.product.imageContents?.find(
      (img) => img.storedLink === item.product.imageUrlsList?.[0]
    )?.displayLink || '/luxury-placeholder.jpg'
  }
  alt={item.product.name}
  sx={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  }}
/>

                           
                          </Box>
                          
                          {/* Product Details */}
                          <Box sx={{ 
                            flexGrow: 1,
                            pr: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}>
                            <Box>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 500,
                                color: luxuryStyles.charcoal,
                                mb: 1,
                                fontFamily: 'serif',
                                fontSize: '1.1rem'
                              }}>
                                {item.product.name}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: luxuryStyles.slate,
                                mb: 2,
                                fontSize: '0.9rem',
                                fontFamily: 'serif'
                              }}>
                                {item.product.description || 'Handcrafted luxury item'}
                              </Typography>
                            </Box>

                            {/* Price and Quantity */}
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <Typography variant="h6" sx={{
                                fontWeight: 500,
                                color: luxuryStyles.goldDark,
                                fontFamily: 'serif'
                              }}>
                                {currencySymbol}{(item.product.price.priceAmt * item.quantity).toFixed(2)}
                              </Typography>

                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: `1px solid ${luxuryStyles.ivory}`,
                                borderRadius: '6px',
                                overflow: 'hidden',
                                backgroundColor: luxuryStyles.ivory
                              }}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onQuantityChange(item.product.primaryId, item.quantity - 1);
                                  }}
                                  disabled={item.quantity <= 1}
                                  sx={{
                                    color: luxuryStyles.charcoal,
                                    borderRadius: 0,
                                    '&:hover': {
                                      backgroundColor: 'rgba(184, 142, 47, 0.1)'
                                    },
                                    '&:disabled': {
                                      color: luxuryStyles.ivory
                                    }
                                  }}
                                >
                                  <Remove className="h-4 w-4" />
                                </IconButton>
                                <Typography sx={{ 
                                  px: 2,
                                  color: luxuryStyles.charcoal,
                                  fontWeight: 500,
                                  fontFamily: 'serif'
                                }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onQuantityChange(item.product.primaryId, item.quantity + 1);
                                  }}
                                  disabled={item.quantity >= 10}
                                  sx={{
                                    color: luxuryStyles.charcoal,
                                    borderRadius: 0,
                                    '&:hover': {
                                      backgroundColor: 'rgba(184, 142, 47, 0.1)'
                                    },
                                    '&:disabled': {
                                      color: luxuryStyles.ivory
                                    }
                                  }}
                                >
                                  <Add className="h-4 w-4" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Remove Button */}
                          <IconButton
                            onClick={() => onRemoveItem(item.product.primaryId)}
                            sx={{
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              color: luxuryStyles.slate,
                              '&:hover': {
                                color: luxuryStyles.gold
                              }
                            }}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                        {index < items.length - 1 && (
                          <Divider sx={{ 
                            mx: 3,
                            borderColor: luxuryStyles.ivory
                          }} />
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </Box>
              </Box>
            </Grid>

            {/* Summary Column */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ 
                position: 'sticky',
                top: 24,
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                border: `1px solid ${luxuryStyles.ivory}`
              }}>
                <Box sx={{ 
                  p: 3,
                  backgroundColor: luxuryStyles.gold,
                  color: 'white'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500,
                    fontFamily: 'serif',
                    letterSpacing: '0.5px'
                  }}>
                    Order Summary
                  </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                  {/* Luxury Benefits */}
                  <Box sx={{ 
                    mb: 3,
                    p: 2.5,
                    backgroundColor: luxuryStyles.ivory,
                    borderRadius: '8px'
                  }}>
                    
                   
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedUser style={{ 
                        color: luxuryStyles.gold,
                        marginRight: 12
                      }} />
                      <Typography variant="body2" sx={{ 
                        color: luxuryStyles.charcoal,
                        fontFamily: 'serif'
                      }}>
                        Authenticity guaranteed
                      </Typography>
                    </Box>
                  </Box>

                  {/* Pricing Breakdown */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1.5
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: luxuryStyles.slate,
                        fontFamily: 'serif'
                      }}>
                        Subtotal
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500,
                        fontFamily: 'serif'
                      }}>
                        {currencySymbol}{totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1.5
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: luxuryStyles.slate,
                        fontFamily: 'serif'
                      }}>
                        Shipping
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500,
                        fontFamily: 'serif',
                        color: luxuryStyles.success
                      }}>
                        FREE
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: luxuryStyles.slate,
                        fontFamily: 'serif'
                      }}>
                        Tax
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500,
                        fontFamily: 'serif'
                      }}>
                        {currencySymbol}{tax.toFixed(2)}
                      </Typography>
                    </Box>
                    <RetailDiscountCode
                      appliedDiscount={appliedDiscount}
                      currencySymbol={currencySymbol}
                      discountAmount={discountAmount}
                      discountCode={discountCode}
                      error={discountError}
                      isApplying={isApplyingDiscount}
                      isDisabled={isProcessing}
                      onApply={onApplyDiscount}
                      onChange={onDiscountCodeChange}
                      onRemove={onRemoveDiscount}
                    />
                    {discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body1" sx={{ color: luxuryStyles.success, fontFamily: 'serif' }}>
                          Discount
                        </Typography>
                        <Typography variant="body1" sx={{ color: luxuryStyles.success, fontFamily: 'serif', fontWeight: 500 }}>
                          -{currencySymbol}{Number(discountAmount).toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ 
                      my: 2,
                      borderColor: luxuryStyles.ivory
                    }} />
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2
                    }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 500,
                        fontFamily: 'serif'
                      }}>
                        Total
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 500,
                        fontFamily: 'serif',
                        color: luxuryStyles.goldDark
                      }}>
                        {currencySymbol}{finalTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Checkout Button */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={onCheckout}
                      disabled={isProcessing || isApplyingDiscount}
                      endIcon={<ShoppingBag />}
                      sx={{
                        py: 1.5,
                        mb: 2,
                        fontSize: '1rem',
                        fontWeight: 500,
                        backgroundColor: luxuryStyles.gold,
                        color: 'white',
                        borderRadius: '8px',
                        fontFamily: 'serif',
                        letterSpacing: '0.5px',
                        '&:hover': {
                          backgroundColor: luxuryStyles.goldDark
                        },
                        '&:disabled': {
                          backgroundColor: luxuryStyles.slate
                        }
                      }}
                    >
                      {isProcessing ? 'Securing Purchase...' : 'Complete Your Order'}
                    </Button>
                  </motion.div>

                  <Typography variant="body2" sx={{ 
                    textAlign: 'center',
                    color: luxuryStyles.slate,
                    mt: 1,
                    fontFamily: 'serif'
                  }}>
                    or{' '}
                    <Button
onClick={() => window.history.back()}
                      sx={{
                        textTransform: 'none',
                        color: luxuryStyles.gold,
                        fontWeight: 500,
                        p: 0,
                        minWidth: 'auto',
                        fontFamily: 'serif',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      continue shopping
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ) : (
          /* Empty State */
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 4,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
            border: `1px solid ${luxuryStyles.ivory}`
          }}>
            <ShoppingBag style={{ 
              width: 80,
              height: 80,
              color: luxuryStyles.ivory,
              marginBottom: 24
            }} />
            <Typography variant="h4" sx={{ 
              fontWeight: 500,
              color: luxuryStyles.charcoal,
              mb: 2,
              fontFamily: 'serif'
            }}>
              Your Luxury Collection Awaits
            </Typography>
            <Typography variant="body1" sx={{ 
              color: luxuryStyles.slate,
              mb: 4,
              maxWidth: '500px',
              fontFamily: 'serif'
            }}>
              Discover our exclusive selection of premium goods, each piece curated for exceptional quality.
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={onContinueShopping}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  backgroundColor: luxuryStyles.gold,
                  color: 'white',
                  borderRadius: '8px',
                  fontFamily: 'serif',
                  letterSpacing: '0.5px',
                  '&:hover': {
                    backgroundColor: luxuryStyles.goldDark
                  }
                }}
              >
                Begin Your Collection
              </Button>
            </motion.div>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SFCart;
